import { pathEnum } from '@/enums/pathEnum';
import ToolBarImp, { ToolbarType, VisibleButtonsImp } from '@/interfaces/ToolBarImp';
import React, { createContext, useContext, useState } from 'react';
import useCurrentAccount from './currentAccount';
import { useSelector } from 'react-redux';
import { ApplicationState } from '@/store';
import _ from 'lodash';
import INomenclature from '@/interfaces/NomenclatureImp';
import IndexesService from '@/services/IndexesService';
import moment from 'moment';
import { useFactors } from './factors';

const ToolbarHookContext = createContext<ToolBarImp | null>(null);

export const initialVisibleButtons: VisibleButtonsImp = {
  view: false,
  delete: false,
  isDeductInterestFirst: false,
  new: false,
  open: false,
  print: false,
  save: false,
  reload: false,
  undo: false,
  calculator: false,
  export: false,
  import: false,
};

export const ToolbarHookProvider = ({ children }: { children: React.ReactElement }) => {
  const [visible, setVisible] = useState(false);
  const [type, setType] = useState<pathEnum>();
  const [visibleButtons, setVisibleButtons] = useState<VisibleButtonsImp>(initialVisibleButtons);
  const [isDeductInterestFirst, setIsDeductInterestFirst] = useState<boolean | undefined>(true);
  const { onCalc, onSave, account, author, feeFines } = useCurrentAccount();
  const { ability } = useSelector((state: ApplicationState) => state.auth);
  const [currentAccount, setCurrentAccount] = useState<ToolbarType>();
  const [automatedAccount, setAutomatedAccount] = useState<ToolbarType>();
  const [nomenclatures, setNomenclatures] = useState<ToolbarType>();
  const { allMemcalcs } = useFactors();

  const deleteFile = () => {};

  const newFile = () => {
    switch (type) {
      case pathEnum.CURRENT_ACCOUNT:
        if (currentAccount?.new) currentAccount.new();
        break;
      case pathEnum.AUTOMATED_UPDATE:
        if (automatedAccount?.new) automatedAccount.new();
        break;
    }
  };
  const open = () => {
    switch (type) {
      case pathEnum.CURRENT_ACCOUNT:
        if (currentAccount?.open) currentAccount.open();
        break;
    }
  };
  const print = () => {
    switch (type) {
      case pathEnum.CURRENT_ACCOUNT:
        if (!currentAccount?.print) return;
        currentAccount.print();
        break;
    }
  };
  const reload = () => {
    switch (type) {
      case pathEnum.CURRENT_ACCOUNT:
        if (!currentAccount?.reload) return;
        currentAccount.reload();
        break;
      case pathEnum.NOMENCLATURE:
        if (!nomenclatures?.reload) return;
        nomenclatures.reload();
        break;
    }
  };

  const save = async () => {
    switch (type) {
      case pathEnum.CURRENT_ACCOUNT: {
        const newCurrentAccount = await onSave({ isNewAccount: Boolean(account.current.id?.length) });
        if (newCurrentAccount.id?.length && currentAccount?.save) currentAccount.save(newCurrentAccount.id);
        break;
      }

      case pathEnum.AUTOMATED_UPDATE: {
        automatedAccount?.save && (await automatedAccount.save());
        break;
      }
    }
  };

  const undo = () => {
    switch (type) {
      case pathEnum.CURRENT_ACCOUNT:
        if (!currentAccount?.undo) return;
        currentAccount.undo();
        break;
    }
  };

  const calculator = async (nomenclatures: INomenclature[]) => {
    switch (type) {
      case pathEnum.CURRENT_ACCOUNT: {
        const memcalcs = allMemcalcs?.[account.current.indexId] || [];
        await onCalc({ origin: 'calc', nomenclatures, isDeductInterestFirst, memcalcs });
        break;
      }
    }
  };

  const view = async (nomenclatures: INomenclature[]) => {
    switch (type) {
      case pathEnum.CURRENT_ACCOUNT: {
        const memcalcs = allMemcalcs?.[account.current.indexId] || [];
        await onCalc({ origin: 'view', nomenclatures, isDeductInterestFirst, memcalcs });
        break;
      }
    }
  };

  const importCalc = () => {
    switch (type) {
      case pathEnum.CURRENT_ACCOUNT:
        if (!currentAccount?.importCalc) return;
        currentAccount.importCalc();
        break;
    }
  };

  const exportCalc = (codified: boolean) => {
    const name = account.current.name;
    const newName = name.includes('(Importado)') ? name : `(Importado) ${name}`;

    const newAccount = {
      ...account.current,
      name: newName,
    };

    const payload = {
      account: newAccount,
      authors: author.list,
      feeFines,
      codified,
      from: 'SEI',
      rules: _.cloneDeep(ability.rules),
    };

    switch (type) {
      case pathEnum.CURRENT_ACCOUNT:
        if (!currentAccount?.exportCalc) return;
        currentAccount.exportCalc(payload);
        break;
    }
  };

  return (
    <ToolbarHookContext.Provider
      value={{
        visible,
        type,
        isDeductInterestFirst,
        importCalc,
        setIsDeductInterestFirst,
        exportCalc,
        setCurrentAccount,
        setNomenclatures,
        setType,
        setVisible,
        delete: deleteFile,
        new: newFile,
        open,
        print,
        visibleButtons,
        setVisibleButtons,
        reload,
        save,
        undo,
        calculator,
        setAutomatedAccount,
        view,
      }}>
      {children}
    </ToolbarHookContext.Provider>
  );
};

export const useToolbar = (): ToolBarImp => {
  const toolbar = useContext(ToolbarHookContext);

  if (!toolbar) {
    throw new Error('useToolbar deve ser usado dentro de um ToolbarHookProvider');
  }

  return toolbar;
};
